/**
 * useDebounce Hook
 *
 * Custom React hook that debounces a value, delaying updates until after
 * a specified delay period has passed without the value changing.
 * Useful for search inputs and API calls to reduce excessive requests.
 *
 * @module hooks/useDebounce
 */

import { useState, useEffect } from 'react';
import type { UseDebounceReturn } from '@/types/dashboard';

/**
 * Debounce a value with a specified delay
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns Object containing the debounced value and debouncing state
 *
 * @example
 * ```typescript
 * const SearchComponent = () => {
 *   const [searchTerm, setSearchTerm] = useState('');
 *   const { debouncedValue, isDebouncing } = useDebounce(searchTerm, 300);
 *
 *   useEffect(() => {
 *     if (debouncedValue) {
 *       // Perform search with debounced value
 *       performSearch(debouncedValue);
 *     }
 *   }, [debouncedValue]);
 *
 *   return (
 *     <div>
 *       <input
 *         value={searchTerm}
 *         onChange={(e) => setSearchTerm(e.target.value)}
 *         placeholder="Search..."
 *       />
 *       {isDebouncing && <span>Searching...</span>}
 *     </div>
 *   );
 * };
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): UseDebounceReturn<T> {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

  useEffect(() => {
    // Set debouncing state to true when value changes
    if (value !== debouncedValue) {
      setIsDebouncing(true);
    }

    // Set up the timeout to update the debounced value
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
      setIsDebouncing(false);
    }, delay);

    // Cleanup function to clear the timeout if value changes before delay
    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delay, debouncedValue]);

  // Handle initial state where value equals debouncedValue
  useEffect(() => {
    if (value === debouncedValue) {
      setIsDebouncing(false);
    }
  }, [value, debouncedValue]);

  return {
    debouncedValue,
    isDebouncing,
  };
}

export default useDebounce;